import { Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State } from '@stencil/core'
import { Component } from '@stencil/core'
import { throttle } from 'lodash-es'
import Mousetrap from 'mousetrap'

import { IBookmark } from '../../../../bookmarks/types'
import { copyToClipboard } from '../../../../call/clipboard/clipboard'
import { IIndividualCue, INote, ISpeaker } from '../../../../call/types'
import { classes, colorClasses } from '../../../../utils/css'
import {
  AnnotationStore,
  findNextCue,
  getFirstNoteCue,
  getFirstSpeakerCue,
  getNoteCues,
  getNoteForCue,
  hasMatchingNote,
  isFirstNoteCue,
  isNoteCue,
  isProcessingCue,
  isSelectable,
  isSelected,
} from '../annotation'
import { CommentStore } from '../comment'
import {
  CallStore,
  changeCueSpeaker,
  editCueText,
  isTrimmingCue,
  nextSearchResult,
  NotesStore,
  PlaybackStore,
  prevSearchResult,
  TranscriptCue,
  TranscriptStore,
  trimCue,
} from '../state'
import { BookmarkButton, DragButton, NoteHeader } from './ui'

@Component({
  tag: 'page-call-detail-transcript',
})
export class Transcript {
  @Element() el: HTMLPageCallDetailTranscriptElement

  @Prop() copyTranscriptUrl: string

  @Event() userScrolled: EventEmitter<number>
  @Event() noteDeleted: EventEmitter<INote>
  @Event() noteEdited: EventEmitter<INote>
  @Event() noteAdded: EventEmitter<INote>

  @State() autoScrollDirection: string
  @State() bookmarkMenuReferenceEl: HTMLElement
  @State() highlightCue: TranscriptCue
  @State() bookmarkCue: TranscriptCue
  @State() hoverCue: TranscriptCue
  @State() isSavingCues: boolean

  editedCues: Array<{ cue: IIndividualCue; text: string }> = []

  @Listen('click', { target: 'window' })
  onOutsideClick() {
    if (this.bookmarkMenuReferenceEl || AnnotationStore.state.editCue) {
      this.resetAnnotations()
    }
  }

  hasAgreedToTrimRecording = false

  scrollEl: HTMLUiScrollareaElement

  onManualScroll = throttle(({ detail: scrollPct }: CustomEvent<number>) => {
    TranscriptStore.state.autoScroll = false
    TranscriptStore.state.scrollPct = scrollPct
    this.bookmarkMenuReferenceEl = null
    this.autoScrollDirection = scrollPct > PlaybackStore.state.pctElapsed ? 'up' : 'down'
  }, 1000 / 10)
  selectionStartCue: TranscriptCue
  mousetrap: Mousetrap.MousetrapInstance

  removeActiveCueListener: () => void
  removeScrollCueListener: () => void
  removeAnnotationListener: () => void
  removeNotesListener: () => void

  dragMode: 'start' | 'end'
  saveAnnotation: boolean
  focusNote: INote
  bookmarkMenuEl: HTMLUiPopperElement

  resetAnnotations() {
    this.highlightCue = null
    this.bookmarkCue = null
    this.bookmarkMenuReferenceEl = null
    AnnotationStore.state.selectedCues = []
    AnnotationStore.state.processingBookmark = null
    AnnotationStore.state.editCue = null
    AnnotationStore.state.selectedNote = null
    this.editedCues = []
  }

  scrollToCue(cue = TranscriptStore.state.activeCue, force = false) {
    if (cue && this.scrollEl) {
      if (force || TranscriptStore.state.autoScroll) {
        const cueIndex = TranscriptStore.state.cues.indexOf(cue)
        let scrollCueEl: HTMLElement
        // The specified cue may not exist in the dom (for action items, etc)
        // so we start there and search for one that does exist
        TranscriptStore.state.cues
          .slice(cueIndex)
          .find((cue) => (scrollCueEl = this.el.querySelector(`[data-cue-id='${cue.id}']`)))
        if (scrollCueEl) {
          this.scrollEl.scrollToElement(scrollCueEl)
        }
      }
    }
  }

  toggleAutoScroll() {
    TranscriptStore.state.autoScroll = !TranscriptStore.state.autoScroll
    if (TranscriptStore.state.autoScroll) {
      this.scrollToCue(TranscriptStore.state.activeCue)
    }
  }

  componentWillLoad() {
    this.removeActiveCueListener = TranscriptStore.onChange('activeCue', (cue) => {
      this.scrollToCue(cue)
    })
    this.removeScrollCueListener = TranscriptStore.onChange('scrollCue', (cue) => {
      this.scrollToCue(cue, true)
    })
    this.removeNotesListener = CallStore.onChange('notes', (notes) => {
      const { processingNote } = AnnotationStore.state
      if (processingNote && !processingNote.id) {
        this.focusNote = notes.find((n) => {
          return n.end_time === processingNote.end_time && n.start_time === processingNote.start_time
        })
      }
      this.resetAnnotations()
    })
  }

  connectedCallback() {
    this.mousetrap = new Mousetrap()
    this.mousetrap.bind('escape', () => this.resetAnnotations())
  }

  disconnectedCallback() {
    this.mousetrap.reset()
    this.removeActiveCueListener()
    this.removeScrollCueListener()
    this.removeNotesListener()
  }

  async confirmComment() {
    CommentStore.state.selectedCue = AnnotationStore.state.selectedCues[0].cues[0]

    this.resetAnnotations()
  }

  async confirmBookmark(bookmark: IBookmark) {
    window.Sentry?.setContext('Bookmark', bookmark)

    this.bookmarkMenuReferenceEl = null

    AnnotationStore.state.isUpdating = true
    AnnotationStore.state.processingBookmark = bookmark

    const { selectedCues } = AnnotationStore.state

    let { selectedNote: note } = AnnotationStore.state
    const { start_time } = selectedCues[0]
    const { end_time } = bookmark.highlight ? selectedCues.slice(-1)[0] : { end_time: start_time - 1 }

    if (note) {
      note = { ...note, start_time, end_time, bookmark } as INote
      AnnotationStore.state.processingNote = note
      this.noteEdited.emit(note)
    } else {
      note = { start_time, end_time, bookmark, text: '', timestamp: null } as INote
      window.Sentry?.setContext('Note', note)
      AnnotationStore.state.processingNote = note
      AnnotationStore.state.processingCue = selectedCues[0]
      this.noteAdded.emit(note)
    }
  }

  render() {
    let cueColor: string
    let currentNote: INote
    // using this as a proxy for whether the user account has team edition enabled:
    const hasTeamEdition = CallStore.state.actions.createCommentUrl
    const showAddAnnotations =
      !CallStore.state.readonly && !AnnotationStore.state.selectedCues?.some((cue) => isNoteCue(cue))
    return (
      <Host class='flex overflow-hidden relative flex-col'>
        <div
          class={`
            absolute bottom-0 z-menu right-0 transition duration-200 transform
            ${classes(TranscriptStore.state.autoScroll || PlaybackStore.state.paused, 'translate-y-full')}
          `}
          style={{ left: '150px' }}
        >
          <button
            onClick={(e) => {
              e.stopImmediatePropagation()
              this.toggleAutoScroll()
            }}
            class='group flex overflow-hidden items-center py-1 px-2 m-auto text-surface-base bg-link rounded-t-lg transition duration-200'
          >
            <ui-icon class='text-lg' icon={this.autoScrollDirection === 'up' ? 'arrow-up-short' : 'arrow-down-short'} />
            <span class='flex-grow mr-1 ml-1 text-sm font-semibold truncate'>Resume Auto-Scroll</span>
          </button>
        </div>
        <section
          hidden={PlaybackStore.state.displayMode !== 'normal'}
          class='flex absolute top-0 left-0 z-40 p-3 w-full pointer-events-none spacing-x-2'
          style={{ paddingLeft: '160px' }}
        >
          {this.copyTranscriptUrl && (
            <button
              class={`
                flex items-center justify-between pointer-events-auto p-1 pl-2 pr-2 font-bold whitespace-no-wrap rounded-md
              text-link normal-case hover:text-surface-base
                ${colorClasses({ bg: '#031319' }, { hover: { bg: 'link' } })}
              `}
              onClick={async () => {
                await copyToClipboard(this.copyTranscriptUrl, false)
              }}
            >
              <span>Copy Transcript</span>
              <ui-icon icon='clipboard' class='pl-2 text-xl' />
            </button>
          )}
          <ui-input
            class='ml-auto w-2/5 bg-surface-base border-surface-highest shadow-contrast pointer-events-auto'
            placeholder='Search Transcript'
            style={{ minWidth: '11ch' }}
            rounded
            size='sm'
            icon='search'
            onInput={(e) => (TranscriptStore.state.searchQuery = (e.target as HTMLInputElement).value)}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                nextSearchResult()
                this.scrollToCue(TranscriptStore.state.foundCue, true)
              }
              if (e.key === 'ArrowDown') {
                nextSearchResult()
                this.scrollToCue(TranscriptStore.state.foundCue, true)
              }
              if (e.key === 'ArrowUp') {
                prevSearchResult()
                this.scrollToCue(TranscriptStore.state.foundCue, true)
              }
            }}
          >
            <button
              hidden={TranscriptStore.state.searchQuery.length === 0}
              class='flex justify-center items-center text-base text-glyph-base opacity-25 hover:opacity-50'
              onClick={(e) => {
                e.stopImmediatePropagation()
                TranscriptStore.state.searchQuery = ''
              }}
            >
              <ui-icon icon='x-circle' />
            </button>
          </ui-input>
        </section>
        <div
          class='absolute top-0 left-0 w-full h-full bg-black'
          style={{ marginLeft: PlaybackStore.state.displayMode !== 'normal' ? '130px' : '150px' }}
        ></div>

        <ui-scrollarea
          class='flex-1'
          shadowColor='transparent'
          shadowStyleY={{ display: 'none' }}
          ref={(el) => (this.scrollEl = el)}
          onUserScrolled={this.onManualScroll}
          overflowX={false}
        >
          {/* Begin Annotations UI */}
          <div
            class='pb-3 sm:pb-6 spacing-y-3'
            style={{ marginTop: PlaybackStore.state.displayMode === 'normal' ? '75px' : '10px', paddingLeft: '150px' }}
          >
            {TranscriptStore.state.transcript.map((cues: TranscriptCue[], dialogueIndex) => {
              const firstSpeakerCue = getFirstSpeakerCue(cues)
              const host = firstSpeakerCue.is_host
              let currentImmutableNote: INote
              return (
                <figure
                  key={`dialogue-${dialogueIndex}`}
                  class={`
                    relative flex flex-col px-4 -mt-2
                    ${classes(host, 'items-end')}
                  `}
                >
                  {cues.map((cue, cueIndex) => {
                    if (cue.id === 0) {
                      // Placeholder/fake cue for immutable bookmarks
                      if (cue.note && (!currentImmutableNote || !hasMatchingNote(cue, currentImmutableNote))) {
                        const {
                          note,
                          note: { bookmark },
                        } = cue
                        // Store this to make sure we don't show any potential duplicates
                        currentImmutableNote = note
                        // A bunch of logic just to get the correct color (or no color) for
                        // the left border, basically, figure out if this cue
                        // is wrapped by another note and use it's color for the
                        // border with some extra consideration when/if the user
                        // is currently dragging/annotating
                        if (currentNote) {
                          // Figure out if there is a following cue with the
                          // current note and if it's selected
                          const nextCue = findNextCue(cue, (c) => {
                            if (c.note && c.note.id === currentNote.id) {
                              return isSelectable(c) ? isSelected(c) : true
                            }
                          })
                          cueColor = isSelected(cue)
                            ? cueColor // If this cue is selected use the color
                            : nextCue && nextCue.note && nextCue.note.bookmark.hex_color // If the following cue is part of the currentNote use the color
                        } else {
                          // Otherwise don't show the left border
                          cueColor = null
                        }
                        return (
                          <NoteHeader
                            onDelete={() => {
                              this.noteDeleted.emit(note)
                            }}
                            onEdit={(newNote) => {
                              this.noteEdited.emit(newNote)
                            }}
                            clip={NotesStore.state.noteClips.find((clip) => clip.note.id === note.id)}
                            autoFocus={note.id === this.focusNote?.id}
                            onAutoFocus={() => {
                              this.focusNote = null
                            }}
                            class='pt-12px pb-2 -mt-1'
                            style={{
                              filter: AnnotationStore.state.editCue ? 'brightness(30%)' : null,
                            }}
                          >
                            {cueColor && (
                              <div
                                class={`
                                  border-l-3 text-transparent absolute bottom-0 left-0 top-0 mt-1 ml-8px
                                  ${colorClasses({ border: cueColor })}
                                `}
                              />
                            )}
                            <div class='absolute top-0 left-0 mt-9px ml-3 sm:-ml-1px text-surface-base'>
                              {bookmark.action_item ? (
                                <ui-icon
                                  class={`
                                    z-10 border border-black p-3px rounded
                                    ${colorClasses({ bg: bookmark.hex_color })}
                                  `}
                                  icon='checkbox-thick'
                                />
                              ) : (
                                <ui-icon
                                  class={`
                                    z-10 border border-black p-3px rounded-full
                                    ${colorClasses({ bg: bookmark.hex_color })}
                                  `}
                                  icon='bookmark-outline-thick'
                                />
                              )}
                            </div>
                          </NoteHeader>
                        )
                      }
                    } else {
                      const note = (currentNote = getNoteForCue(cue))
                      const annotate = note ? note.bookmark.highlight : false
                      const isEditing = !!AnnotationStore.state.editCue
                      const isEditCue = AnnotationStore.state.editCue === cue
                      const showHeader = cueIndex === 0
                      cueColor = note
                        ? note.bookmark.hex_color
                        : isProcessingCue(cue)
                        ? AnnotationStore.state.processingBookmark?.hex_color
                        : 'surface-higher'

                      return (
                        <Fragment>
                          {isFirstNoteCue(cue) && (
                            <NoteHeader
                              onDelete={() => {
                                this.noteDeleted.emit(note)
                              }}
                              onEdit={(newNote) => {
                                this.noteEdited.emit(newNote)
                              }}
                              onChangeHighlight={(el) => {
                                AnnotationStore.state.selectedNote = note
                                AnnotationStore.state.selectedCues = getNoteCues(note)
                                this.bookmarkMenuEl.position = 'top'
                                this.bookmarkMenuReferenceEl = el
                              }}
                              clip={NotesStore.state.noteClips.find((clip) => clip.note.id === note.id)}
                              autoFocus={note.id === this.focusNote?.id}
                              onAutoFocus={() => {
                                this.focusNote = null
                              }}
                              class='transition duration-200'
                              style={{
                                filter: isEditing ? 'brightness(30%)' : null,
                              }}
                            >
                              {annotate && (
                                <Fragment>
                                  <div
                                    class={`
                                      border-l-3 text-transparent absolute bottom-0 left-0 top-0 mt-1 ml-23px sm:ml-8px
                                      ${colorClasses({ border: cueColor })}
                                    `}
                                  />
                                  <DragButton
                                    class={`
                                      z-10 absolute top-0 mt-3px left-0 text-surface-base ml-3 sm:ml-0
                                      ${colorClasses({ bg: cueColor })}
                                    `}
                                    key={`drag-up-${note.id}`}
                                    direction='up'
                                    disabled={CallStore.state.readonly}
                                    onMouseEnter={() => (this.scrollEl.allowDragging = true)}
                                    onMouseLeave={() => (this.scrollEl.allowDragging = false)}
                                    onMouseDown={() => this.startNoteDrag(cue, 'start')}
                                  />
                                </Fragment>
                              )}
                            </NoteHeader>
                          )}
                          <page-call-detail-transcript-quote
                            annotate={annotate}
                            cue={cue}
                            speakers={CallStore.state.speakers}
                            cueColor={cueColor}
                            data-cue-id={cue.id}
                            firstSpeakerCue={firstSpeakerCue}
                            host={host}
                            isEditCue={isEditCue}
                            isEditing={isEditing}
                            isProcessing={isProcessingCue(cue)}
                            isTrimming={isTrimmingCue(cue)}
                            isSavingCues={this.isSavingCues}
                            isActive={this.hoverCue === cue}
                            key={`cue-${cue.id}`}
                            showHeader={showHeader}
                            showAddMenu={
                              (!this.hoverCue && cue === TranscriptStore.state.activeCue) ||
                              cue === this.hoverCue ||
                              AnnotationStore.state.selectedCues?.includes(cue)
                            }
                            hasAddMenu={
                              (!CallStore.state.readonly && !isNoteCue(cue)) ||
                              !!CallStore.state.actions.createCommentUrl
                            }
                            onMouseEnter={() => {
                              if (isEditing || this.bookmarkMenuReferenceEl) {
                                return
                              }
                              NotesStore.state.highlightId = cue.note ? cue.note.id : null
                              this.hoverCue = cue
                              if (this.dragMode && isSelectable(cue) && AnnotationStore.state.startCue) {
                                if (this.dragMode === 'start') {
                                  AnnotationStore.state.startCue = cue
                                } else {
                                  AnnotationStore.state.endCue = cue
                                }
                              }
                            }}
                            onAddBookmark={({ detail: el }) => {
                              this.bookmarkMenuEl.position = 'right'
                              AnnotationStore.state.selectedCues = [cue]
                              this.bookmarkMenuReferenceEl = el
                            }}
                            onCueEdited={({ detail }) => {
                              this.onCueEdit(detail.cue, detail.text)
                            }}
                            onCuesSaved={() => {
                              this.saveEditedCues()
                            }}
                            onSpeakerChanged={({ detail }) => {
                              this.onSpeakerEdit(cue, detail)
                            }}
                            onDurationTrimmed={(event) => {
                              if (TranscriptStore.state.isTrimming) {
                                return
                              }
                              const cueWithDuration = { ...cue, ...event.detail }
                              if (this.hasAgreedToTrimRecording) {
                                trimCue(cueWithDuration)
                              } else {
                                AnnotationStore.state.trimCue = cueWithDuration
                              }
                            }}
                            onDragActive={({ detail }) => {
                              this.scrollEl.allowDragging = detail
                            }}
                            onStartDrag={() => {
                              this.startNoteDrag(cue, 'end')
                            }}
                            onPlayCue={() => {
                              TranscriptStore.state.autoScroll = true
                              PlaybackStore.state.seekTime = cue.start_time
                            }}
                          />
                        </Fragment>
                      )
                    }
                  })}
                </figure>
              )
            })}
          </div>
          {!!CallStore.state.call.processing_duration && (
            <div class='flex flex-col spacing-y-3' style={{ marginTop: '75px', paddingLeft: '150px' }}>
              <ui-progress
                color='glyph-base'
                class='mx-auto mt-5 mb-8 h-5 text-2xs font-bold leading-none rounded-full'
                style={{ width: '18ch' }}
                mockSeconds={CallStore.state.call.processing_duration}
              >
                <span class='flex absolute justify-center items-center pt-1px w-full h-full text-xs font-bold text-center text-surface-base uppercase bg-white bg-opacity-50'>
                  PROCESSING
                </span>
              </ui-progress>
            </div>
          )}
        </ui-scrollarea>
        {/* highlight selector menu */}
        <ui-popper
          ref={(el) => (this.bookmarkMenuEl = el)}
          visible={!!this.bookmarkMenuReferenceEl}
          position='right'
          margin={15}
          reference={this.bookmarkMenuReferenceEl}
        >
          <ui-pointer width={16} height={8} fillColor='surface-base' strokeColor='black' strokeWidth={1} />
          <div class='flex flex-col py-2 bg-surface-base rounded-lg border border-black shadow-lg -spacing-y-3px'>
            {!AnnotationStore.state.selectedNote && showAddAnnotations && (
              <Fragment>
                {CallStore.state.bookmarks
                  .filter((b) => !b.highlight)
                  .map((bookmark) => (
                    <BookmarkButton
                      bookmark={bookmark}
                      class='py-1 px-3'
                      onClick={(e) => {
                        e.stopImmediatePropagation()
                        this.confirmBookmark(bookmark)
                      }}
                    />
                  ))}
                {CallStore.state.bookmarks.filter((b) => !b.highlight) && (
                  <div class='pb-2 mt-2 border-t border-surface-high' />
                )}
              </Fragment>
            )}
            <Fragment>
              <button
                class={`flex items-center py-1 px-3 hover:bg-surface-upper transition duration-200 select-none spacing-x-2
                  ${classes(!hasTeamEdition, 'text-glyph-light cursor-not-allowed')}`}
                onClick={(e) => {
                  e.stopImmediatePropagation()
                  this.confirmComment()
                }}
                disabled={!hasTeamEdition}
              >
                <ui-icon icon='chat-outline' />
                <span class='text-base font-extrabold uppercase'>Comment</span>
                {!hasTeamEdition && <ui-infotip>Only available on Fathom Team Edition</ui-infotip>}
              </button>
              {showAddAnnotations && CallStore.state.bookmarks.filter((b) => b.highlight) && (
                <div class='pb-2 mt-2 border-t border-surface-high' />
              )}
            </Fragment>
            {showAddAnnotations &&
              CallStore.state.bookmarks
                .filter((b) => b.highlight)
                .map((bookmark) => (
                  <BookmarkButton
                    class='py-1 px-3'
                    bookmark={bookmark}
                    onClick={(e) => {
                      e.stopImmediatePropagation()
                      this.confirmBookmark(bookmark)
                    }}
                  />
                ))}
          </div>
        </ui-popper>
        {AnnotationStore.state.trimCue && (
          <ui-dialog
            content={({ dialog }) => (
              <div
                class={`flex flex-col border-info-error border-2 items-center relative text-center py-6 px-3 justify-center rounded-md bg-surface-upper text-md`}
                style={{ maxWidth: '640px' }}
              >
                <ui-icon
                  onClick={() => {
                    AnnotationStore.state.trimCue = null
                    this.hasAgreedToTrimRecording = false
                    dialog.close()
                  }}
                  class='absolute top-0 right-0 mt-1 mr-1 text-3xl text-surface-highest hover:text-glyph-base cursor-pointer'
                  icon='x'
                />
                <p class='spacing-y-6'>
                  This action cannot be undone.
                  <br />
                  <br />
                  This will permanently delete this portion of the call.
                </p>
                <button
                  class='py-1 px-3 mt-3 text-base font-bold bg-info-error rounded-md'
                  type='submit'
                  onClick={() => {
                    this.hasAgreedToTrimRecording = true
                    dialog.close()
                    trimCue(AnnotationStore.state.trimCue)
                    AnnotationStore.state.trimCue = null
                  }}
                >
                  Yes, delete this portion of the call
                </button>
              </div>
            )}
          />
        )}
      </Host>
    )
  }

  async saveEditedCues() {
    this.isSavingCues = true
    await Promise.all([
      // Save all pending cue edits
      ...this.editedCues.map(({ cue, text }) => editCueText(cue, text)),
      // Wait for the store to get updated so edits are reflected in the ui
      new Promise<void>((resolve) => {
        const removeListener = TranscriptStore.onChange('cues', () => {
          removeListener()
          resolve()
        })
      }),
    ])
    this.isSavingCues = false
    this.resetAnnotations()
  }

  onCueEdit(realCue: IIndividualCue, text: string) {
    const i = this.editedCues.findIndex(({ cue }) => cue.id === realCue.id)
    if (i > -1) {
      if (text === realCue.text) {
        this.editedCues.splice(i, 1)
      } else {
        this.editedCues[i].text = text
      }
    } else if (text !== realCue.text) {
      this.editedCues.push({ cue: realCue, text })
    }
  }

  async onSpeakerEdit(cue: TranscriptCue, speaker: ISpeaker) {
    AnnotationStore.state.processingCue = cue
    this.isSavingCues = true
    await Promise.all([
      ...cue.cues.map((realCue) => changeCueSpeaker(realCue, speaker)),
      new Promise<void>((resolve) => {
        const removeListener = TranscriptStore.onChange('cues', () => {
          removeListener()
          resolve()
        })
      }),
    ])
    this.isSavingCues = false
    this.resetAnnotations()
  }

  startNoteDrag = (cue: TranscriptCue, mode: this['dragMode']) => {
    if (NotesStore.state.isSaving || !cue.note) {
      return
    }

    window.Sentry?.setContext('Cue', cue)

    const first = getFirstNoteCue(cue.note)
    AnnotationStore.state.startCue = first
    this.dragMode = mode

    document.body.addEventListener('mouseup', this.endNoteDrag, { once: true, capture: true })
    this.removeAnnotationListener = AnnotationStore.onChange('selectedCues', () => {
      this.saveAnnotation = true
    })
  }

  endNoteDrag = () => {
    this.removeAnnotationListener()
    this.dragMode = null
    if (this.saveAnnotation) {
      this.saveAnnotation = false
      const bookmark = AnnotationStore.state.selectedNote?.bookmark
      if (bookmark) {
        this.confirmBookmark(bookmark)
      }
    }
  }
}
